import React, {Dispatch, SetStateAction, useRef, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {
  MarvinChemicalFormat,
  MarvinDisplaySettings,
  MarvinEvent,
  marvinJSLicenseSource,
  marvinJSSource,
  MarvinSketcher,
  CIP_STEREO_INFO_URL,
  CLEAN_URL,
  HYDROGENIZE_URL,
  MOLCONVERT_URL,
  MARVIN_FORMAT_TO_CHEMICAL_FILE_FORMAT,
} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import {getMarvinFromIFrameNode} from 'chemistry/marvinjs-wrappers/MarvinJSHelpers';
import {detectReaction} from 'chemistry/molecules/MoleculeUtil';
import {token} from 'CSRF';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';

export type ChemicalStructureDrawerValue = string | null;

function setStructure(
  value: ChemicalStructureDrawerValue,
  format: MarvinChemicalFormat | null,
  sketcher: MarvinSketcher
): void {
  if (value == null) {
    sketcher.clear();
  } else {
    sketcher.importStructure(format, value);
  }
}

async function updateHasReactionContents(
  setHasReactionContents: ((value: boolean) => void) | undefined,
  mrvExportPromise: Promise<string>
): Promise<void> {
  if (setHasReactionContents != null) {
    const mrvStructure = await mrvExportPromise;
    const hasReactionContents = await detectReaction(
      mrvStructure,
      MARVIN_FORMAT_TO_CHEMICAL_FILE_FORMAT[MarvinChemicalFormat.MRV]
    );
    setHasReactionContents(hasReactionContents);
  }
}

/**
 * Custom React Hook that parent components can use to keep track of state when rendering ChemicalStructureDrawer.
 * Lightweight for now but will eventually include logic for specifying reaction options, requesting export format, etc.
 */
export function useControlledChemicalStructureDrawer(): [
  ChemicalStructureDrawerValue,
  Dispatch<SetStateAction<ChemicalStructureDrawerValue>>,
] {
  return useState<ChemicalStructureDrawerValue>(null);
}

export function useReactionDetection(): [boolean, Dispatch<SetStateAction<boolean>>] {
  return useState<boolean>(false);
}

export enum ChemicalStructureDrawerMode {
  STANDARD = 'STANDARD',
  REACTION = 'REACTION',
  BASIC_SEARCH = 'BASIC_SEARCH',
}

// See https://marvinjs-demo.chemaxon.com/latest/examples/example-toolbars_with_displaysettings.html for a
// list of available MarvinJS modes.
const MARVINJS_MODE_FROM_MODE: {[key in ChemicalStructureDrawerMode]: MarvinDisplaySettings['toolbars']} = {
  [ChemicalStructureDrawerMode.STANDARD]: 'standard',
  [ChemicalStructureDrawerMode.REACTION]: 'reporting',
  [ChemicalStructureDrawerMode.BASIC_SEARCH]: 'basicSearch',
};

export interface ChemicalStructureDrawerProps {
  onMoleculeChange: (value: ChemicalStructureDrawerValue) => void;
  setHasReactionContents?: (value: boolean) => void;
  initialValue: ChemicalStructureDrawerValue;
  mode: ChemicalStructureDrawerMode;
  inputFormat?: MarvinChemicalFormat | null;
  outputFormat?: MarvinChemicalFormat;
}

/**
 * Component for rendering a MarvinJS chemical drawer and receiving the value of the drawer as a molfile.
 * Encapsulates marvin initialization and provides a standardized wrapper around its functionality.
 * Documentation is found at https://marvinjs-demo.chemaxon.com/latest/jsdoc.html
 *
 * Note that while this component accepts an initial value and receives the value from MarvinJS, it's
 * not a true controlled component because the parent does not set the state of the inner iframe. Making
 * this a true controlled component messes up MarvinJS' undo/redo system because it adds an extra frame into
 * the change stack, and provides a jarring experience by recentering the molecule on each update.
 * Until we know how to prevent that, this should stay semi-controlled.
 */
export function ChemicalStructureDrawer({
  initialValue,
  onMoleculeChange,
  mode,
  setHasReactionContents,
  inputFormat = MarvinChemicalFormat.MOL,
  outputFormat = MarvinChemicalFormat.MOL,
}: ChemicalStructureDrawerProps): JSX.Element {
  const iframeNodeRef = useRef<HTMLIFrameElement | null>(null);

  const [marvinLoadError, setMarvinLoadError] = useState(false);

  function setUpMarvin(): void {
    const node = iframeNodeRef.current;
    if (node == null) {
      notifications.error('Attempted to set up MarvinJS onLoad but node was null');
      setMarvinLoadError(true);
      return;
    }
    const marvin = getMarvinFromIFrameNode(node);
    if (marvin == null) {
      notifications.error('Attempted to load MarvinJS from iFrame but it was not set in the iframe window');
      setMarvinLoadError(true);
      return;
    }
    marvin.onReady(() => {
      // Set license file dynamically
      marvin.Sketch.license(marvinJSLicenseSource, true);
      if (marvin.sketcherInstance) {
        const marvinSketch = marvin.sketcherInstance;
        marvinSketch.setServices({
          molconvertws: MOLCONVERT_URL,
          stereoinfows: CIP_STEREO_INFO_URL,
          clean2dws: CLEAN_URL,
          hydrogenizews: HYDROGENIZE_URL,
        });
        marvin.Sketch.addRequestInterceptor((config) => {
          config.setRequestHeader('X-CSRFToken', token);
        });

        marvinSketch.setDisplaySettings({toolbars: MARVINJS_MODE_FROM_MODE[mode]});

        const updateMolecule = async (): Promise<void> => {
          // `setHasReactionContents` always relies on MRV format if it's specified while `onMoleculeChange` only relies on it
          // when specified as the output format. Avoid double-exporting when the same export can be used for both.
          if (setHasReactionContents != null || outputFormat === MarvinChemicalFormat.MRV) {
            const mrvStructurePromise = marvinSketch.exportStructure(MarvinChemicalFormat.MRV);
            updateHasReactionContents(setHasReactionContents, mrvStructurePromise);
            if (outputFormat === MarvinChemicalFormat.MRV) {
              onMoleculeChange(await mrvStructurePromise);
            }
          }
          if (outputFormat !== MarvinChemicalFormat.MRV) {
            const molStructure = await marvinSketch.exportStructure(outputFormat);
            onMoleculeChange(molStructure);
          }
        };

        // This logic is buggy: when using undo, `updateMolecule` is called twice.
        // First for the undo, then again for the MOLCHANGE event. We should remove the undo/redo
        // listeners or debounce the call.
        marvinSketch.on(MarvinEvent.MOLCHANGE, updateMolecule);
        marvinSketch.on(MarvinEvent.UNDO, updateMolecule);
        marvinSketch.on(MarvinEvent.REDO, updateMolecule);

        // Load initial structure after event listeners are registered in order to invoke
        // `onMoleculeChange` and `setHasReactionContents` callbacks with the initial structure.
        if (initialValue != null) {
          setStructure(initialValue, inputFormat, marvinSketch);
        }
      }
    });
  }

  if (!BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE) {
    return <div>You do not have permission to use the chemical structure drawer</div>;
  }

  if (marvinLoadError) {
    return <div>An error occurred when attempting to load MarvinJS</div>;
  }

  return (
    <iframe
      ref={iframeNodeRef}
      title="marvinjs"
      id="sketch"
      src={marvinJSSource}
      onLoad={setUpMarvin}
      style={{
        overflow: 'hidden',
        // Expand to fit the containing DOM node, which callers can size as desired.
        flex: 1,
        border: 'none',
      }}
      {...getTestProps({component: 'ChemicalStructureDrawer', element: 'root'})}
    />
  );
}
